import React from 'react';
import { FloorTheme } from '@/lib/themes';

interface AvatarProps {
    src?: string;
    username: string;
    size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
    theme: FloorTheme;
    className?: string;
    onClick?: () => void;
}

export const Avatar: React.FC<AvatarProps> = ({ src, username, size = 'md', theme, className = '', onClick }) => {
    const sizeClasses = {
        sm: 'w-8 h-8 text-xs',
        md: 'w-12 h-12 text-sm',
        lg: 'w-24 h-24 text-xl',
        xl: 'w-32 h-32 text-2xl',
        '2xl': 'w-48 h-48 text-3xl'
    };

    const initial = username ? username[0].toUpperCase() : '?';

    // Determining Frame Style based on theme radius/shadow
    // "Deep" logic: 1-3F have no radius, 4-6F have partial radius, 7-10F are full circles with glow.
    // We rely on theme.radius and theme.shadow mainly.

    // Additional Glow Logic for High Floors (7-10F & Admin)
    const isLuxury = theme.radius === 'rounded-3xl' || theme.radius === 'rounded-full' || theme.name === 'The Control Room';
    const isGod = theme.name === 'The Heaven' || theme.name === 'The Control Room';
    const isPentagon = theme.avatarShape === 'pentagon';
    const isHexagon = theme.avatarShape === 'hexagon';
    const isOctagon = theme.avatarShape === 'octagon';
    const isDiamond = theme.avatarShape === 'diamond';
    const isEye = theme.avatarShape === 'eye';

    const isPolygon = isPentagon || isHexagon || isOctagon || isDiamond || isEye;

    const frameClasses = isPolygon
        ? 'rounded-none'
        : theme.avatarShape === 'square' ? 'rounded-none'
            : theme.avatarShape === 'circle' ? 'rounded-full'
                : theme.avatarShape === 'rounded' ? 'rounded-lg' : theme.radius;

    const clipPath = isPentagon
        ? 'polygon(50% 0%, 100% 38%, 82% 100%, 18% 100%, 0% 38%)'
        : isHexagon
            ? 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)'
            : isOctagon
                ? 'polygon(30% 0%, 70% 0%, 100% 30%, 100% 70%, 70% 100%, 30% 100%, 0% 70%, 0% 30%)'
                : isDiamond
                    ? 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)'
                    : isEye
                        ? 'polygon(0% 50%, 50% 15%, 100% 50%, 50% 85%)' // Sharp Eye / Wide Diamond
                        : undefined;

    return (
        <div
            className={`relative flex items-center justify-center overflow-hidden border-2 transition-all duration-300
                ${frameClasses} ${theme.shadow} ${sizeClasses[size]} ${className}
                ${isLuxury && !isPolygon ? 'ring-2 ring-offset-2' : ''}
                ${isGod ? 'animate-pulse ring-4' : ''}
            `}
            style={{
                borderColor: theme.colors.primary,
                backgroundColor: theme.colors.card,
                color: theme.colors.text,
                cursor: onClick ? 'pointer' : 'default',
                boxShadow: isGod ? `0 0 20px ${theme.colors.accent}` : undefined,
                clipPath: clipPath,
                border: isPolygon ? 'none' : undefined,
                //@ts-ignore
                '--tw-ring-color': theme.colors.accent,
                '--tw-ring-offset-color': theme.colors.background
            }}
            onClick={onClick}
        >
            {/* Shape Border Simulation */}
            {isPolygon && (
                <div className="absolute inset-0 z-10 pointer-events-none"
                    style={{
                        boxShadow: `inset 0 0 0 2px ${theme.colors.border}`,
                        clipPath: clipPath
                    }}
                />
            )}

            {/* Special Double Border for Admin (Complex Shapes) */}
            {(isHexagon || isOctagon || isDiamond || isEye) && (
                <div className="absolute inset-0 z-20 pointer-events-none"
                    style={{
                        boxShadow: `inset 0 0 0 4px ${theme.colors.accent}`,
                        opacity: 0.5,
                        clipPath: clipPath
                    }}
                />
            )}

            {src ? (
                <img src={src} alt={username} className="w-full h-full object-cover" />
            ) : (
                <div
                    className={`font-bold flex items-center justify-center w-full h-full ${theme.font}`}
                    style={{ backgroundColor: theme.colors.secondary }}
                >
                    {initial}
                </div>
            )}

            {/* Glossy Overlay for Luxury Tiers */}
            {(isLuxury || isGod) && !isPolygon && (
                <div className="absolute inset-0 bg-gradient-to-tr from-white/20 to-transparent pointer-events-none" />
            )}
        </div>
    );
};
